import React, { createContext, useEffect } from 'react';

import { MerchantBankDetails, getMerchantProfile } from 'src/services/merchant-profile.service';
import { SetTealiumMerchantDetails } from '../helpers/tealium-events';
import { queue } from 'src/helpers/delay';
import { IMerchant, IMerchantDetails } from 'src/common/types';
import toastMessageServiceSingleton from 'src/services/toast-message.service';

interface IInitState {
  merchant: IMerchant;
  merchantDetails: IMerchantDetails;
  merchantBankDetails: MerchantBankDetails;
}

interface IState {
  merchant: IMerchant;
  merchantDetails: IMerchantDetails;
  setMerchantDetails: (details: IMerchantDetails) => void;
  merchantBankDetails: MerchantBankDetails;
  setMerchantBankDetails: (details: MerchantBankDetails) => void;
}

export enum ActionType {
  MERCHANT_PROFILE = 'MERCHANT_PROFILE',
  MERCHANT_DETAILS = 'MERCHANT_DETAILS',
  MERCHANT_BANK_DETAILS = 'MERCHANT_BANK_DETAILS',
}

export type SetMerchantDetails = {
  type: ActionType.MERCHANT_DETAILS;
  merchantDetails: IMerchantDetails;
};

export type SetMerchantProfile = {
  type: ActionType.MERCHANT_PROFILE;
  merchant: IMerchant;
};

export type SetMerchantBankDetails = {
  type: ActionType.MERCHANT_BANK_DETAILS;
  merchantBankDetails: MerchantBankDetails;
};

export type Action = SetMerchantDetails | SetMerchantProfile | SetMerchantBankDetails;

export const MerchantProfileContext = createContext<IState | undefined>(undefined);

type MerchantProfileProviderProps = {
  children: React.ReactNode;
};

export function MerchantProfileProvider({ children }: MerchantProfileProviderProps) {
  const initState: IInitState = {
    merchant: {} as IMerchant,
    merchantDetails: {} as IMerchantDetails,
    merchantBankDetails: {} as MerchantBankDetails,
  };

  const reducer = (state: IInitState, action: Action): IInitState => {
    switch (action.type) {
      case ActionType.MERCHANT_DETAILS:
        return {
          ...state,
          merchantDetails: action.merchantDetails,
        };

      case ActionType.MERCHANT_PROFILE:
        return {
          ...state,
          merchant: action.merchant,
        };

      case ActionType.MERCHANT_BANK_DETAILS:
        return {
          ...state,
          merchantBankDetails: action.merchantBankDetails,
        };

      default:
        return state;
    }
  };

  const [merchantProfileState, dispatch] = React.useReducer(reducer, initState);

  const merchantProfileContext = React.useMemo(
    () => ({
      setMerchantDetails: (details: IMerchantDetails) =>
        dispatch({ type: ActionType.MERCHANT_DETAILS, merchantDetails: details }),
      setMerchantBankDetails: (details: MerchantBankDetails) =>
        dispatch({ type: ActionType.MERCHANT_BANK_DETAILS, merchantBankDetails: details }),
    }),
    [],
  );

  const getMerchantAsync = async (abortController: AbortController) => {
    const response = await getMerchantProfile(abortController);
    if (!response.ok) {
      toastMessageServiceSingleton.addError(response?.message, null, '', false, null, true);
      return;
    }

    if (response.ok && !abortController.signal.aborted) {
      dispatch({
        type: ActionType.MERCHANT_PROFILE,
        merchant: response.result,
      });

      SetTealiumMerchantDetails(
        response.result?.merchantId || 'unset',
        response.result?.merchantType || 'unset',
      );
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    queue.push(() => getMerchantAsync(abortController));

    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <MerchantProfileContext.Provider value={{ ...merchantProfileContext, ...merchantProfileState }}>
      {children}
    </MerchantProfileContext.Provider>
  );
}
